import type { User } from "@ferrivia/api-contract";
import { useTracker } from "@ferrivia/auth-web";
import { BuildVersion, Button, Dialog, DialogClose, DialogContent, DialogTitle, Text } from "@ferrivia/ui";
import { useTranslation } from "@ferrivia/i18n";
import { initials } from "../../user";
import s from "./AccountSheet.module.css";

export interface AccountSheetProps {
  user: User;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSignOut: () => void;
}

/**
 * Who am I signed in as, and how do I get out. The nav rail carries this on
 * desktop, but it is hidden below 960px, so the tab bar opens this sheet
 * instead — otherwise there is no way off the console on a phone.
 *
 * Controlled by its opener so the trigger can live in whichever shell needs
 * it and keep that shell's own styling.
 *
 * apps/client has its own near-twin of the body below (ProfileMenu, a bottom
 * sheet behind home's top-right trigger). The duplication is deliberate —
 * see that component's note for what differs and why the shared body was not
 * promoted to packages/ui.
 */
export function AccountSheet({ user, open, onOpenChange, onSignOut }: AccountSheetProps) {
  const { t } = useTranslation("clinic");
  const tracker = useTracker();

  function signOut() {
    tracker.track("clinic.signed_out", { surface: "account_sheet" });
    onOpenChange(false);
    onSignOut();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={s.sheet} aria-describedby={undefined}>
        <DialogTitle className={s.title}>{t("accountSheet.title", "Account")}</DialogTitle>

        <div className={s.identity}>
          <span className={s.avatar} aria-hidden="true">
            {initials(user)}
          </span>
          <div className={s.who}>
            <Text weight="medium" className={s.email}>
              {user.email ?? t("navRail.noEmail", "No email")}
            </Text>
            <Text size="sm" className={s.role}>
              {user.role}
            </Text>
          </div>
        </div>

        <div className={s.actions}>
          {/* primary, not secondary: sign out is this sheet's whole point, so
              it gets the filled treatment. */}
          <Button variant="primary" className={s.action} onClick={signOut}>
            {t("navRail.signOut", "Sign out")}
          </Button>
          <DialogClose asChild>
            <Button variant="ghost" className={s.action}>
              {t("accountSheet.close", "Close")}
            </Button>
          </DialogClose>
        </div>

        <BuildVersion
          env={import.meta.env.VITE_APP_ENV}
          version={import.meta.env.VITE_APP_VERSION}
          className={s.buildVersion}
        />
      </DialogContent>
    </Dialog>
  );
}
